import { useEffect, useRef } from 'react'
import { useBibleStore } from '../stores/bibleStore'
import { useAuthStore } from '../stores/authStore'
import { useAudioBibleStore } from '../stores/audioBibleStore'
import { useBibleAudioPlayer } from '../hooks/useBibleAudioPlayer'
import { useAudioResumeSaver } from '../hooks/useAudioResumeSaver'
import { track } from '../lib/analytics'
import { OT_BOOKS, NT_BOOKS, getBookById, getNextChapter, getPrevChapter } from '../lib/bibleBooks'
import type { BibleBook } from '../lib/bibleBooks'
import { TRANSLATIONS } from '../lib/bibleConstants'
import { BibleMiniPlayer } from '../components/BibleMiniPlayer'
import { BibleFullScreenPlayer } from '../components/BibleFullScreenPlayer'
import { ResumeChip } from '../components/ResumeChip'
import { registerMediaSessionSubscriber } from '../lib/mediaSession'

function BookGrid({ books, onSelect }: { books: BibleBook[]; onSelect: (id: string) => void }) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {books.map((book) => (
        <button
          key={book.id}
          onClick={() => onSelect(book.id)}
          className="bg-elevated hover:bg-surface-hover text-text-primary text-sm py-2.5 px-2 rounded-lg transition-colors text-left truncate"
        >
          {book.name}
        </button>
      ))}
    </div>
  )
}

function BookSelector() {
  const { selectBook } = useBibleStore()

  return (
    <div className="space-y-5">
      <section>
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Old Testament</h2>
        <BookGrid books={OT_BOOKS} onSelect={selectBook} />
      </section>
      <section>
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">New Testament</h2>
        <BookGrid books={NT_BOOKS} onSelect={selectBook} />
      </section>
    </div>
  )
}

function ChapterSelector() {
  const { selectedBookId, selectChapter, goBack } = useBibleStore()
  const book = selectedBookId ? getBookById(selectedBookId) : null

  if (!book) return null

  const chapters = Array.from({ length: book.chapters }, (_, i) => i + 1)

  return (
    <div>
      <button
        onClick={goBack}
        className="flex items-center gap-1 text-accent text-sm mb-4 hover:text-accent-hover transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        All Books
      </button>
      <h2 className="text-lg font-semibold mb-3">{book.name}</h2>
      <div className="grid grid-cols-6 gap-2">
        {chapters.map((ch) => (
          <button
            key={ch}
            onClick={() => selectChapter(ch)}
            className="bg-elevated hover:bg-surface-hover text-text-primary text-sm py-3 rounded-lg transition-colors font-medium"
          >
            {ch}
          </button>
        ))}
      </div>
    </div>
  )
}

function ChapterReader() {
  const { selectedBookId, selectedChapter, chapterContent, loading, error, translation, setTranslation, goNext, goPrev, goBack } = useBibleStore()
  const contentRef = useRef<HTMLDivElement>(null)
  const book = selectedBookId ? getBookById(selectedBookId) : null

  const hasNext = selectedBookId && selectedChapter ? !!getNextChapter(selectedBookId, selectedChapter) : false
  const hasPrev = selectedBookId && selectedChapter ? !!getPrevChapter(selectedBookId, selectedChapter) : false

  const audioBookId = useAudioBibleStore((s) => s.bookId)
  const audioChapter = useAudioBibleStore((s) => s.chapter)
  const audioStatus = useAudioBibleStore((s) => s.status)
  const currentVerse = useAudioBibleStore((s) => s.currentVerse)
  const playAudio = useAudioBibleStore((s) => s.play)
  const voiceId = useAudioBibleStore((s) => s.voiceId)
  const isAudioActiveHere =
    audioBookId === selectedBookId && audioChapter === selectedChapter && audioStatus !== 'idle'

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [chapterContent])

  // Wrap verse spans on every chapter render so we can highlight the active verse
  useEffect(() => {
    if (!contentRef.current || !chapterContent) return
    const root = contentRef.current
    const verseSpans = root.querySelectorAll('span.v[data-number]')
    verseSpans.forEach((span) => {
      const verseNum = span.getAttribute('data-number')
      if (!verseNum) return
      // Mark the containing element so we can style it
      let container: Element | null = span.parentElement
      while (container && container.getAttribute('data-verse') === null && container !== root) {
        if (container.tagName === 'P' || container.tagName === 'DIV') {
          container.setAttribute('data-verse', verseNum)
          break
        }
        container = container.parentElement
      }
    })
  }, [chapterContent])

  // Apply highlight class when currentVerse changes
  useEffect(() => {
    if (!contentRef.current || !isAudioActiveHere) return
    const root = contentRef.current
    const all = root.querySelectorAll('[data-verse]')
    all.forEach((el) => el.classList.remove('bible-verse-active'))
    if (currentVerse != null) {
      const target = root.querySelector(`[data-verse="${currentVerse}"]`)
      if (target) {
        target.classList.add('bible-verse-active')
        target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [currentVerse, isAudioActiveHere, chapterContent])

  const onListen = () => {
    if (!selectedBookId || !selectedChapter) return
    track('bible_audio_play_clicked', { book_id: selectedBookId, chapter: selectedChapter, translation, voice_id: voiceId })
    void playAudio(selectedBookId, selectedChapter, translation, { voiceId })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 shrink-0 space-y-2">
        <div className="flex items-center justify-between">
          <button
            onClick={goBack}
            className="flex items-center gap-1 text-accent text-sm hover:text-accent-hover transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            {book?.name}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onListen}
              aria-label="Listen to this chapter"
              className="flex items-center gap-1 bg-elevated hover:bg-surface-hover text-text-primary text-sm rounded-lg px-2.5 py-1.5 border border-surface transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 106 0V4a3 3 0 00-3-3zM5 10v2a7 7 0 006 6.92V21h-2v2h6v-2h-2v-2.08A7 7 0 0019 12v-2h-2v2a5 5 0 01-10 0v-2H5z" />
              </svg>
              <span>Listen</span>
            </button>
            <select
              value={translation}
              onChange={(e) => setTranslation(e.target.value)}
              className="bg-elevated text-text-primary text-sm rounded-lg px-2 py-1.5 border border-surface focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {TRANSLATIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
        <h1 className="text-xl font-bold">
          {book?.name} {selectedChapter}
        </h1>
      </div>

      {/* Content */}
      <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-center py-16">
            <p className="text-error mb-2">{error}</p>
            <button
              onClick={() => {
                if (selectedBookId && selectedChapter)
                  useBibleStore.getState().goToChapter(selectedBookId, selectedChapter)
              }}
              className="text-accent text-sm hover:text-accent-hover transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && chapterContent && (
          <>
            <div
              className="bible-content text-text-primary leading-relaxed text-base"
              dangerouslySetInnerHTML={{ __html: chapterContent.content }}
            />

            {/* Navigation */}
            <div className="flex items-center justify-between py-6 mt-4 border-t border-surface">
              {hasPrev ? (
                <button
                  onClick={goPrev}
                  className="flex items-center gap-1 text-accent text-sm hover:text-accent-hover transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                  Previous
                </button>
              ) : <div />}
              {hasNext ? (
                <button
                  onClick={goNext}
                  className="flex items-center gap-1 text-accent text-sm hover:text-accent-hover transition-colors"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              ) : <div />}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default function Bible() {
  const { view, translation, setTranslation } = useBibleStore()
  const { user } = useAuthStore()
  const loadVoices = useAudioBibleStore((s) => s.loadVoices)

  useBibleAudioPlayer()
  useAudioResumeSaver()

  useEffect(() => {
    track('bible_page_visited')
  }, [])

  useEffect(() => {
    void loadVoices()
    const unsub = registerMediaSessionSubscriber()
    return () => {
      unsub()
    }
  }, [loadVoices])

  // Initialize translation from user profile on first load
  useEffect(() => {
    if (user?.profile.default_bible_translation && useBibleStore.getState().translation === 'KJV') {
      const profileTranslation = user.profile.default_bible_translation
      if ((TRANSLATIONS as readonly string[]).includes(profileTranslation)) {
        setTranslation(profileTranslation)
      }
    }
    if (user?.profile && (user.profile as { default_audio_voice?: string }).default_audio_voice) {
      useAudioBibleStore.setState({
        voiceId: (user.profile as { default_audio_voice?: string }).default_audio_voice as string,
      })
    }
  }, [user, setTranslation])

  if (view === 'reading') {
    return (
      <>
        <ChapterReader />
        <BibleMiniPlayer />
        <BibleFullScreenPlayer />
      </>
    )
  }

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
          <h1 className="text-xl font-bold">Bible</h1>
          {view === 'books' && (
            <select
              value={translation}
              onChange={(e) => setTranslation(e.target.value)}
              className="bg-elevated text-text-primary text-sm rounded-lg px-2 py-1.5 border border-surface focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {TRANSLATIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
          {view === 'books' && (
            <>
              <ResumeChip />
              <BookSelector />
            </>
          )}
          {view === 'chapters' && <ChapterSelector />}
        </div>
      </div>
      <BibleMiniPlayer />
      <BibleFullScreenPlayer />
    </>
  )
}
